﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import {
  Calendar,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileText,
  Trash2,
  Download,
  Ban,
  Fingerprint,
  Send,
  Paperclip,
} from 'lucide-react';
import { formatDateTimeIST, monthDateRange } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';
import { leaveService } from '@/services/leave.service';
import { deleteAccessService } from '@/services/deleteAccess.service';
import { toast } from 'sonner';
import type { LeaveRequest, ShortLeaveRequest } from '@pms/shared';

type ApprovalTab = 'regular' | 'short' | 'short_hist' | 'regular_hist';

const TAB_TITLES: Record<ApprovalTab, string> = {
  regular: 'Pending Approvals',
  short: 'Short Leave Approvals',
  short_hist: 'Short Leave History',
  regular_hist: 'Regular Leave History',
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}


function formatTime(t: string) {
  const [h, m] = t.split(':');
  const hour = parseInt(h ?? '0', 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 || 12;
  return `${display}:${m} ${suffix}`;
}

function formatDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

function EmployeeAvatar({ firstName, lastName, photoUrl }: { firstName?: string | null; lastName?: string | null; photoUrl?: string }) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" className="w-9 h-9 rounded-full object-cover" />;
  }
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '??';
  return (
    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold text-sm">
      {initials}
    </div>
  );
}

interface ReviewDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (remarks: string) => void;
  action: 'approved' | 'rejected';
  loading: boolean;
}

function ReviewDialog({ open, title, onClose, onConfirm, action, loading }: ReviewDialogProps) {
  const [remarks, setRemarks] = useState('');
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className={action === 'approved' ? 'text-emerald-700' : 'text-red-700'}>
            {action === 'approved' ? 'Approve' : 'Reject'} Leave Request
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{title}</p>
        <div className="mt-2">
          <Label className="text-sm font-medium text-foreground mb-1.5 block">
            Remarks {action === 'rejected' && <span className="text-destructive">*</span>}
            {action === 'approved' && <span className="text-muted-foreground">(optional)</span>}
          </Label>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add remarks..."
            rows={3}
            className="resize-none text-sm"
          />
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(remarks)}
            disabled={loading || (action === 'rejected' && !remarks.trim())}
            className={
              action === 'approved'
                ? 'bg-teal-600 hover:bg-teal-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : action === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Regular Leave Card ───────────────────────────────────────────────────────

interface RegularCardProps {
  req: LeaveRequest;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  actionLoading: boolean;
}

function RegularLeaveCard({ req, onApprove, onReject, actionLoading }: RegularCardProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm p-3.5 space-y-2.5">
      {/* Row 1: avatar + name + leave type + step */}
      <div className="flex items-center gap-2.5">
        <EmployeeAvatar
          firstName={req.target_first_name}
          lastName={req.target_last_name}
          photoUrl={req.target_photo_url ?? undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">
              {req.target_first_name} {req.target_last_name}
            </span>
            {req.target_employee_code && (
              <span className="text-xs text-muted-foreground">{req.target_employee_code}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }}
            />
            <span className="text-xs font-medium text-muted-foreground">{req.leave_type_name}</span>
            {(req.total_approval_steps ?? 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold">
                Step {req.current_approval_step}/{req.total_approval_steps}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: dates + days */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(req.start_date)}{req.start_date !== req.end_date ? ` – ${formatDate(req.end_date)}` : ''}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {req.days_count} day{req.days_count !== 1 ? 's' : ''}
          {req.is_half_day && ` (${req.half_day_period})`}
        </span>
      </div>

      {/* Row 3: reason */}
      <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <span className="break-words min-w-0">{req.reason}</span>
      </div>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Send className="w-3 h-3" />
        Requested · {formatDateTimeIST(req.created_at)}
      </p>
      {req.document_url && (
        <a
          href={req.document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
        >
          <Paperclip className="w-3 h-3" />
          View document
        </a>
      )}

      {/* Action buttons — only for a reviewer the backend will accept */}
      {req.can_act ? (
        <div className="flex gap-2 pt-0.5">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
            disabled={actionLoading}
            onClick={() => onReject(req.id)}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            Reject
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white"
            disabled={actionLoading}
            onClick={() => onApprove(req.id)}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Approve
          </Button>
        </div>
      ) : (
        <WaitingForApprovers approvers={req.pending_approvers} />
      )}
    </div>
  );
}

/** Shown in place of Approve/Reject when the viewer is not one of the request's approvers. */
function WaitingForApprovers({ approvers }: { approvers?: string[] | null }) {
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground">
      <Clock className="w-3.5 h-3.5 mt-px shrink-0" />
      <span>
        {approvers && approvers.length > 0
          ? `Waiting for: ${approvers.join(', ')}`
          : 'Waiting for the assigned approver'}
      </span>
    </p>
  );
}

// ─── Short Leave Card ─────────────────────────────────────────────────────────

interface ShortCardProps {
  sl: ShortLeaveRequest;
  showActions?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  actionLoading?: boolean;
  onDelete?: (id: string) => void;
}

function ShortLeaveCard({ sl, showActions, onApprove, onReject, actionLoading, onDelete }: ShortCardProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm p-3.5 space-y-2.5">
      {/* Row 1: avatar + name + duration badge */}
      <div className="flex items-center gap-2.5">
        <EmployeeAvatar
          firstName={sl.target_first_name}
          lastName={sl.target_last_name}
          photoUrl={sl.target_photo_url ?? undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm text-foreground truncate">
              {sl.target_first_name} {sl.target_last_name}
            </span>
            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-semibold">
              <Clock className="w-3 h-3" />
              {formatDuration(sl.duration_minutes)}
            </span>
          </div>
          {sl.target_employee_code && (
            <span className="text-xs text-muted-foreground">{sl.target_employee_code}</span>
          )}
        </div>
      </div>

      {/* Row 2: date + time range */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(sl.request_date)}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {formatTime(sl.from_time)} – {formatTime(sl.to_time)}
        </span>
      </div>

      {/* Row 3: reason */}
      <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <span className="break-words min-w-0">{sl.reason}</span>
      </div>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Send className="w-3 h-3" />
        Requested · {formatDateTimeIST(sl.created_at)}
      </p>

      {/* History: status */}
      {sl.approved_at && (
        <p className="text-[10px] text-emerald-600 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Approved {formatDate(sl.approved_at)}
          {sl.approver_first_name && ` · ${sl.approver_first_name} ${sl.approver_last_name ?? ''}`}
        </p>
      )}
      {sl.status === 'rejected' && !sl.approved_at && (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1">
            <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
            <span className="text-[10px] text-red-600">Rejected · {formatDate(sl.updated_at)}</span>
          </div>
          {sl.rejection_reason && (
            <p className="text-[10px] text-red-500/80 pl-5 italic">"{sl.rejection_reason}"</p>
          )}
        </div>
      )}

      {/* Action buttons — only for a reviewer the backend will accept */}
      {showActions && !sl.can_act && <WaitingForApprovers approvers={sl.pending_approvers} />}
      {showActions && sl.can_act && onApprove && onReject && (
        <div className="flex gap-2 pt-0.5">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
            disabled={actionLoading}
            onClick={() => onReject(sl.id)}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            Reject
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white"
            disabled={actionLoading}
            onClick={() => onApprove(sl.id)}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Approve
          </Button>
        </div>
      )}
      {!showActions && onDelete && (
        <div className="flex justify-end pt-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(sl.id)}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

/**
 * How often the open page re-checks for new or changed requests, so a leave applied
 * from /leave shows up without a manual refresh. Polling, not caching: a background
 * refetch keeps the current rows on screen (isLoading stays false) and swaps them in
 * silently. React Query pauses it while the tab is hidden; refetchOnWindowFocus then
 * catches up the moment the reviewer comes back.
 */
const LIVE_REFRESH_MS = 60_000;

export default function LeaveApprovalsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ApprovalTab>('regular');
  const [search, setSearch] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [filterMonth, setFilterMonth] = useState<number | ''>('');
  const [filterYear,  setFilterYear]  = useState<number>(new Date().getFullYear());

  const monthRange = filterMonth !== '' ? monthDateRange(filterYear, filterMonth) : undefined;
  const dateFrom = monthRange?.from;
  const dateTo   = monthRange?.to;

  // Export — Regular + Short leave + employee summary for the open tab's mode and filters,
  // every page; the server reads live data on each click
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await leaveService.exportApprovalsXlsx({
        status: activeTab === 'regular' || activeTab === 'short' ? 'pending' : undefined,
        search: search || undefined,
        leave_type_id: leaveTypeFilter !== 'all' ? leaveTypeFilter : undefined,
        date_from: dateFrom,
        date_to: dateTo,
      });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    id: string;
    action: 'approved' | 'rejected';
    type: 'regular' | 'short';
    label: string;
  } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    type: 'regular' | 'short';
    label: string;
  } | null>(null);

  // Queries — search/filter passed to API so all pages are searched, not just current
  const regularPendingQuery = useQuery({
    queryKey: ['leave', 'team', 'regular', 'pending', page, search, leaveTypeFilter, dateFrom, dateTo],
    queryFn: () => leaveService.listTeamRequests({
      status: 'pending', page, limit: 20,
      search: search || undefined,
      leave_type_id: leaveTypeFilter !== 'all' ? leaveTypeFilter : undefined,
      date_from: dateFrom, date_to: dateTo,
    }),
    enabled: activeTab === 'regular',
    refetchInterval: LIVE_REFRESH_MS,
  });

  const regularHistQuery = useQuery({
    queryKey: ['leave', 'team', 'regular', 'hist', page, search, leaveTypeFilter, dateFrom, dateTo],
    queryFn: () => leaveService.listTeamRequests({
      page, limit: 20,
      search: search || undefined,
      leave_type_id: leaveTypeFilter !== 'all' ? leaveTypeFilter : undefined,
      date_from: dateFrom, date_to: dateTo,
    }),
    enabled: activeTab === 'regular_hist',
    refetchInterval: LIVE_REFRESH_MS,
  });

  const shortPendingQuery = useQuery({
    queryKey: ['leave', 'team', 'short', 'pending', page, search, dateFrom, dateTo],
    queryFn: () => leaveService.listTeamShortLeaves({
      status: 'pending', page, limit: 20,
      search: search || undefined,
      date_from: dateFrom, date_to: dateTo,
    }),
    enabled: activeTab === 'short',
    refetchInterval: LIVE_REFRESH_MS,
  });

  const shortHistQuery = useQuery({
    queryKey: ['leave', 'team', 'short', 'hist', page, search, dateFrom, dateTo],
    queryFn: () => leaveService.listTeamShortLeaves({
      page, limit: 20,
      search: search || undefined,
      date_from: dateFrom, date_to: dateTo,
    }),
    enabled: activeTab === 'short_hist',
    refetchInterval: LIVE_REFRESH_MS,
  });

  // Header totals — always on and unfiltered, whichever tab is open
  const regularPendingCountQuery = useQuery({
    queryKey: ['leave', 'team', 'regular', 'pending-count'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 1 }),
    refetchInterval: LIVE_REFRESH_MS,
  });

  const shortPendingCountQuery = useQuery({
    queryKey: ['leave', 'team', 'short', 'pending-count'],
    queryFn: () => leaveService.listTeamShortLeaves({ status: 'pending', page: 1, limit: 1 }),
    refetchInterval: LIVE_REFRESH_MS,
  });

  const leaveTypesQuery = useQuery({
    queryKey: ['leave', 'types'],
    queryFn: () => leaveService.listLeaveTypes(),
  });

  const { data: leaveDeletePerm } = useQuery({
    queryKey: ['delete-access', 'my-permission', 'leave'],
    queryFn: () => deleteAccessService.getMyPermission('leave'),
  });
  const canDeleteLeave = leaveDeletePerm?.can_delete ?? false;

  // Mutations
  const reviewRegularMutation = useMutation({
    mutationFn: ({ id, action, remarks }: { id: string; action: 'approved' | 'rejected'; remarks: string }) =>
      leaveService.reviewLeaveRequest(id, { action, remarks: remarks || undefined }),
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'team'] });
      toast.success(`Request ${action}`);
      setReviewDialog(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reviewShortMutation = useMutation({
    mutationFn: ({ id, action, remarks }: { id: string; action: 'approved' | 'rejected'; remarks: string }) =>
      leaveService.reviewShortLeave(id, { action, rejection_reason: remarks || undefined }),
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'team'] });
      toast.success(`Short leave ${action}`);
      setReviewDialog(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteRegularMutation = useMutation({
    mutationFn: (id: string) => leaveService.adminDeleteLeaveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'team'] });
      toast.success('Leave record deleted');
      setDeleteConfirm(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteShortMutation = useMutation({
    mutationFn: (id: string) => leaveService.adminDeleteShortLeave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'team'] });
      toast.success('Short leave record deleted');
      setDeleteConfirm(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function openDelete(id: string, type: 'regular' | 'short', label: string) {
    setDeleteConfirm({ id, type, label });
  }

  function handleDelete() {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'regular') {
      deleteRegularMutation.mutate(deleteConfirm.id);
    } else {
      deleteShortMutation.mutate(deleteConfirm.id);
    }
  }

  function openApprove(id: string, type: 'regular' | 'short', label: string) {
    setReviewDialog({ open: true, id, action: 'approved', type, label });
  }

  function openReject(id: string, type: 'regular' | 'short', label: string) {
    setReviewDialog({ open: true, id, action: 'rejected', type, label });
  }

  function handleConfirm(remarks: string) {
    if (!reviewDialog) return;
    const { id, action, type } = reviewDialog;
    if (type === 'regular') {
      reviewRegularMutation.mutate({ id, action, remarks });
    } else {
      reviewShortMutation.mutate({ id, action, remarks });
    }
  }

  function handleTabChange(tab: ApprovalTab) {
    setActiveTab(tab);
    setPage(1);
    // Search is kept on purpose: a name typed on Regular should still apply on Short/History.
    setLeaveTypeFilter('all');
    setFilterMonth('');
  }

  const regularPending = regularPendingQuery.data?.data ?? [];
  const regularHist    = regularHistQuery.data?.data ?? [];
  const shortPending   = shortPendingQuery.data?.data ?? [];
  const shortHist      = shortHistQuery.data?.data ?? [];

  const awaitingRegular = regularPendingQuery.data?.awaiting_action ?? 0;
  const awaitingShort = shortPendingQuery.data?.awaiting_action ?? 0;

  const isLoading =
    (activeTab === 'regular' && regularPendingQuery.isLoading) ||
    (activeTab === 'regular_hist' && regularHistQuery.isLoading) ||
    (activeTab === 'short' && shortPendingQuery.isLoading) ||
    (activeTab === 'short_hist' && shortHistQuery.isLoading);

  const isError =
    (activeTab === 'regular' && regularPendingQuery.isError) ||
    (activeTab === 'regular_hist' && regularHistQuery.isError) ||
    (activeTab === 'short' && shortPendingQuery.isError) ||
    (activeTab === 'short_hist' && shortHistQuery.isError);

  function refetchCurrent() {
    if (activeTab === 'regular') regularPendingQuery.refetch();
    else if (activeTab === 'regular_hist') regularHistQuery.refetch();
    else if (activeTab === 'short') shortPendingQuery.refetch();
    else shortHistQuery.refetch();
  }

  const currentPagination =
    activeTab === 'regular'
      ? regularPendingQuery.data?.pagination
      : activeTab === 'regular_hist'
        ? regularHistQuery.data?.pagination
        : activeTab === 'short'
          ? shortPendingQuery.data?.pagination
          : shortHistQuery.data?.pagination;

  const currentItems =
    activeTab === 'regular'
      ? regularPending.length
      : activeTab === 'regular_hist'
        ? regularHist.length
        : activeTab === 'short'
          ? shortPending.length
          : shortHist.length;

  const totalItems = currentPagination?.total ?? currentItems;

  const tabItems = [
    { key: 'regular', label: 'Regular' },
    { key: 'short', label: 'Short' },
    { key: 'short_hist', label: 'Short History' },
    { key: 'regular_hist', label: 'Regular History' },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Leave Approvals</h1>
          <p className="text-sm text-muted-foreground">Manage team leave and short leave requests.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleTabChange('regular')}
            className={`flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-2.5 py-1 text-left transition-colors hover:bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 ${activeTab === 'regular' ? 'ring-1 ring-amber-400' : ''}`}
          >
            <Calendar className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex min-w-0 items-baseline gap-1.5">
              <p className="text-[11px] font-medium text-muted-foreground">Regular Pending</p>
              <p className="text-sm font-bold leading-tight text-amber-700 dark:text-amber-400">
                {regularPendingCountQuery.isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : regularPendingCountQuery.isError ? (
                  '—'
                ) : (
                  regularPendingCountQuery.data?.pagination.total ?? 0
                )}
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('short')}
            className={`flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50/60 px-2.5 py-1 text-left transition-colors hover:bg-teal-50 dark:border-teal-500/30 dark:bg-teal-500/10 dark:hover:bg-teal-500/20 ${activeTab === 'short' ? 'ring-1 ring-teal-400' : ''}`}
          >
            <Clock className="h-3.5 w-3.5 shrink-0 text-teal-600 dark:text-teal-400" />
            <div className="flex min-w-0 items-baseline gap-1.5">
              <p className="text-[11px] font-medium text-muted-foreground">Short Pending</p>
              <p className="text-sm font-bold leading-tight text-teal-700 dark:text-teal-400">
                {shortPendingCountQuery.isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : shortPendingCountQuery.isError ? (
                  '—'
                ) : (
                  shortPendingCountQuery.data?.pagination.total ?? 0
                )}
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabItems} active={activeTab} onChange={(k) => handleTabChange(k as ApprovalTab)} />

      {/* Main card */}
      <div className="rounded-lg border border-border/60 bg-card">
        {/* Sub-header */}
        <div className="flex flex-col gap-3 border-b border-border/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-foreground">{TAB_TITLES[activeTab]}</h2>
            <span className="text-xs text-muted-foreground">
              {currentItems} of {totalItems}{' '}
              {activeTab === 'regular' || activeTab === 'short' ? 'request(s)' : 'record(s)'}
            </span>
            {activeTab === 'regular' && awaitingRegular > 0 && (
              <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
                Awaiting your action: {awaitingRegular}
              </span>
            )}
            {activeTab === 'short' && awaitingShort > 0 && (
              <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
                Awaiting your action: {awaitingShort}
              </span>
            )}
            {(activeTab === 'short_hist' || activeTab === 'regular_hist') && (
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                Total: {totalItems}
              </span>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search by name or code…"
              containerClassName="w-52"
            />
            {(activeTab === 'regular' || activeTab === 'regular_hist') &&
              (leaveTypesQuery.data ?? []).length > 0 && (
                <Select value={leaveTypeFilter} onValueChange={(v) => { setLeaveTypeFilter(v); setPage(1); }}>
                  <SelectTrigger className="h-9 w-44 text-sm">
                    <SelectValue placeholder="All leave types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All leave types</SelectItem>
                    {(leaveTypesQuery.data ?? []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            <Select
              value={filterMonth === '' ? 'all' : String(filterMonth)}
              onValueChange={(v) => { setFilterMonth(v === 'all' ? '' : Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue placeholder="All Months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(filterYear)}
              onValueChange={(v) => { setFilterYear(Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-9 w-24 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterMonth !== '' && (
              <Button variant="outline" size="sm" className="h-9 px-2.5" onClick={() => { setFilterMonth(''); setPage(1); }}>
                ✕
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => void handleExport()}
              disabled={exporting}
              title="Regular + Short leave for this tab and filters (all pages), with a per-employee summary"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <AlertCircle className="w-10 h-10 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load requests</p>
              <Button variant="outline" size="sm" onClick={refetchCurrent}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              {/* Regular Pending */}
              {activeTab === 'regular' && (
                <>
                  {regularPending.length === 0 ? (
                    <EmptyState
                      icon={<Calendar className="w-10 h-10 text-muted-foreground/40" />}
                      title="No pending requests"
                      subtitle="All leave requests have been reviewed."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {regularPending.map((req) => (
                        <RegularLeaveCard
                          key={req.id}
                          req={req}
                          onApprove={(id) =>
                            openApprove(
                              id,
                              'regular',
                              `${req.target_first_name} ${req.target_last_name} — ${req.leave_type_name}`,
                            )
                          }
                          onReject={(id) =>
                            openReject(
                              id,
                              'regular',
                              `${req.target_first_name} ${req.target_last_name} — ${req.leave_type_name}`,
                            )
                          }
                          actionLoading={reviewRegularMutation.isPending}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Short Pending */}
              {activeTab === 'short' && (
                <>
                  {shortPending.length === 0 ? (
                    <EmptyState
                      icon={<Clock className="w-10 h-10 text-muted-foreground/40" />}
                      title="No pending short leave requests"
                      subtitle="All short leave requests have been reviewed."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {shortPending.map((sl) => (
                        <ShortLeaveCard
                          key={sl.id}
                          sl={sl}
                          showActions
                          onApprove={(id) =>
                            openApprove(
                              id,
                              'short',
                              `${sl.target_first_name} ${sl.target_last_name}`,
                            )
                          }
                          onReject={(id) =>
                            openReject(
                              id,
                              'short',
                              `${sl.target_first_name} ${sl.target_last_name}`,
                            )
                          }
                          actionLoading={reviewShortMutation.isPending}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Short History */}
              {activeTab === 'short_hist' && (
                <>
                  {shortHist.length === 0 ? (
                    <EmptyState
                      icon={<Clock className="w-10 h-10 text-muted-foreground/40" />}
                      title="No short leave history"
                      subtitle="No approved short leave requests found."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {shortHist.map((sl) => (
                        <ShortLeaveCard
                          key={sl.id}
                          sl={sl}
                          onDelete={canDeleteLeave ? (id) =>
                            openDelete(id, 'short', `${sl.target_first_name} ${sl.target_last_name}`)
                          : undefined}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Regular History */}
              {activeTab === 'regular_hist' && (
                <>
                  {regularHist.length === 0 ? (
                    <EmptyState
                      icon={<Calendar className="w-10 h-10 text-muted-foreground/40" />}
                      title="No regular leave history"
                      subtitle="No approved leave requests found."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {regularHist.map((req) => (
                        <RegularHistCard
                          key={req.id}
                          req={req}
                          onDelete={canDeleteLeave ? (id) =>
                            openDelete(
                              id,
                              'regular',
                              `${req.target_first_name} ${req.target_last_name} — ${req.leave_type_name}`,
                            )
                          : undefined}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Pagination */}
              {currentPagination && currentPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border/40">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPagination.page} of {currentPagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= currentPagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Review Dialog */}
      {reviewDialog && (
        <ReviewDialog
          open={reviewDialog.open}
          title={reviewDialog.label}
          action={reviewDialog.action}
          loading={reviewRegularMutation.isPending || reviewShortMutation.isPending}
          onClose={() => setReviewDialog(null)}
          onConfirm={handleConfirm}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open onOpenChange={(v) => !v && setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Leave Record</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Permanently delete this record for <span className="font-medium text-foreground">{deleteConfirm.label}</span>?
              If it was approved, the used balance will be reversed. This cannot be undone.
            </p>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}
                disabled={deleteRegularMutation.isPending || deleteShortMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteRegularMutation.isPending || deleteShortMutation.isPending}
              >
                {(deleteRegularMutation.isPending || deleteShortMutation.isPending)
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      {icon}
      <p className="text-base font-medium text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function RegularHistCard({ req, onDelete }: { req: LeaveRequest; onDelete?: (id: string) => void }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm p-3.5 space-y-2.5">
      {/* Row 1: avatar + name + leave type */}
      <div className="flex items-center gap-2.5">
        <EmployeeAvatar
          firstName={req.target_first_name}
          lastName={req.target_last_name}
          photoUrl={req.target_photo_url ?? undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">
              {req.target_first_name} {req.target_last_name}
            </span>
            {req.target_employee_code && (
              <span className="text-xs text-muted-foreground">{req.target_employee_code}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }}
            />
            <span className="text-xs font-medium text-muted-foreground">{req.leave_type_name}</span>
          </div>
        </div>
      </div>

      {/* Row 2: dates + days */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(req.start_date)}{req.start_date !== req.end_date ? ` – ${formatDate(req.end_date)}` : ''}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {req.days_count} day{req.days_count !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Row 3: reason */}
      <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <span className="break-words min-w-0">{req.reason}</span>
      </div>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Send className="w-3 h-3" />
        Requested · {formatDateTimeIST(req.created_at)}
      </p>
      {req.document_url && (
        <a
          href={req.document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
        >
          <Paperclip className="w-3 h-3" />
          View document
        </a>
      )}

      {/* Row 4: status + delete */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
          {req.status === 'approved' ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="text-[10px] text-emerald-600">Approved · {formatDate(req.updated_at)}</span>
            </>
          ) : req.status === 'rejected' ? (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                <span className="text-[10px] text-red-600">Rejected · {formatDate(req.updated_at)}</span>
              </div>
              {(() => {
                const remark = req.approval_history?.find((h) => h.action === 'rejected')?.remarks;
                return remark ? (
                  <p className="text-[10px] text-red-500/80 pl-5 italic line-clamp-2 break-all overflow-hidden">"{remark}"</p>
                ) : null;
              })()}
            </div>
          ) : req.status === 'cancelled' ? (
            <div className="space-y-0.5 min-w-0">
              <div className="flex items-center gap-1">
                <Ban className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="text-[10px] text-slate-600 dark:text-slate-300">
                  Cancelled · {formatDate(req.cancelled_at ?? req.updated_at)}
                </span>
              </div>
              {req.cancel_reason && (
                <p className="flex items-start gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
                  <Fingerprint className="w-3 h-3 mt-px shrink-0" />
                  <span className="break-words min-w-0">
                    {req.cancel_reason}. Leave was approved but the employee punched in
                    {' — '}paid credit (if any) was returned.
                  </span>
                </p>
              )}
            </div>
          ) : (
            <>
              <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="text-[10px] text-amber-600 capitalize">{req.status}</span>
            </>
          )}
        </div>
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
            onClick={() => onDelete(req.id)}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
